import type { AiuApu } from '../apus/tipos'

export type TipoInsumoMaestro = 'material' | 'mano_obra' | 'equipo' | 'transporte'

export type InsumoMaestro = {
  id: string
  codigo: string
  nombre: string
  tipo: TipoInsumoMaestro
  unidad: string
  tarifa_base: number
  categoria: string
  activo: boolean
  created_at?: string
  updated_at?: string
}

export type ItemPresupuesto = {
  id: string
  apu_id: string
  codigo: string
  nombre: string
  unidad: string
  cantidad: number
  costo_directo_unitario: number
  precio_unitario: number
  subtotal: number
}

export type CapituloPresupuesto = {
  id: string
  orden: number
  nombre: string
  items: ItemPresupuesto[]
  subtotal_capitulo: number
}

export type VersionPresupuesto = {
  version: number
  fecha: string
  motivo: string
  total: number
  capitulos: CapituloPresupuesto[]
  aiu: AiuApu
}

export type PresupuestoObra = {
  id: string
  codigo: string
  nombre: string
  cliente: string
  ubicacion?: string
  proyecto_id?: string | null
  estado: 'borrador' | 'presentado' | 'aprobado' | 'rechazado'
  aiu: AiuApu
  capitulos: CapituloPresupuesto[]
  costo_directo_total: number
  valor_aiu_total: number
  total_presupuesto: number
  versiones: VersionPresupuesto[]
  activo: boolean
  created_at: string
  updated_at: string
}

/**
 * Recalcula matemáticamente todos los subtotales por ítem, capítulo,
 * costo directo total, AIU global discriminado y precio final de venta.
 */
export function recalcularPresupuesto(
  base: Omit<PresupuestoObra, 'costo_directo_total' | 'valor_aiu_total' | 'total_presupuesto'>
): PresupuestoObra {
  let costoDirectoAcumulado = 0

  const capitulosRecalculados = (base.capitulos || []).map((cap, idx) => {
    let subtotalCapitulo = 0

    const itemsRecalculados = (cap.items || []).map((it) => {
      const cant = Math.max(0, Number(it.cantidad || 0))
      const pUnit = Math.max(0, Number(it.precio_unitario || 0))
      const cDirectoUnit = Math.max(0, Number(it.costo_directo_unitario || pUnit * 0.8))
      const sub = Number((cant * pUnit).toFixed(2))

      subtotalCapitulo += sub
      costoDirectoAcumulado += cant * cDirectoUnit

      return {
        ...it,
        cantidad: cant,
        precio_unitario: pUnit,
        costo_directo_unitario: cDirectoUnit,
        subtotal: sub,
      }
    })

    return {
      ...cap,
      orden: cap.orden || idx + 1,
      items: itemsRecalculados,
      subtotal_capitulo: Number(subtotalCapitulo.toFixed(2)),
    }
  })

  // Cálculo de AIU Global del Presupuesto
  const aiu = base.aiu || {
    porcentaje_admin: 10,
    porcentaje_imprevistos: 0,
    porcentaje_utilidad: 3,
    porcentaje_iva_utilidad: 19,
  }

  const adminPct = Number(aiu.porcentaje_admin || 0) / 100
  const imprevPct = Number(aiu.porcentaje_imprevistos || 0) / 100
  const utilPct = Number(aiu.porcentaje_utilidad || 0) / 100
  const ivaUtilPct = Number(aiu.porcentaje_iva_utilidad || 19) / 100

  const valorAdmin = Number((costoDirectoAcumulado * adminPct).toFixed(2))
  const valorImprevistos = Number((costoDirectoAcumulado * imprevPct).toFixed(2))
  const valorUtilidad = Number((costoDirectoAcumulado * utilPct).toFixed(2))
  const valorIvaUtilidad = Number((valorUtilidad * ivaUtilPct).toFixed(2))
  const valorAiuTotal = Number((valorAdmin + valorImprevistos + valorUtilidad + valorIvaUtilidad).toFixed(2))

  const totalPresupuesto = Number((costoDirectoAcumulado + valorAiuTotal).toFixed(2))

  return {
    ...base,
    aiu,
    capitulos: capitulosRecalculados,
    costo_directo_total: Number(costoDirectoAcumulado.toFixed(2)),
    valor_aiu_total: valorAiuTotal,
    total_presupuesto: totalPresupuesto,
  }
}

